import { Box, BoxProps, Center, IconButton, Image, Spinner, Text } from '@chakra-ui/react'
import { Address } from 'features/valueObjects/Address'
import { Location } from 'features/valueObjects/Location'
import { Photo } from 'features/valueObjects/Photo'
import { NullProps } from 'libs/utils.types'
import { ChevronLeftIcon, ChevronRightIcon } from 'presentation/components/Icons'
import { GOOGLE_KEY } from 'presentation/const/libs.const'
import QueryString from 'qs'
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'

type PropertyPreviewCardProps = BoxProps & {
  address: NullProps<Address>
  location: Location | null
  photos?: Photo[]
  imageWidth?: number
  imageHeight?: number
}

export const PropertyPreviewCard = (props: PropertyPreviewCardProps) => (
  <PropertyPreviewCardBase
    key={createPreviewCardKey(props)}
    {...props}
  />
)

const PropertyPreviewCardBase = ({
  location,
  photos = [],
  imageWidth = 218,
  imageHeight = 126,
  ...props
}: PropertyPreviewCardProps) => {
  const photosWithUrl = useMemo(
    () => photos.filter(photo => !!photo.url?.trim()),
    [photos],
  )

  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [slideDirection, setSlideDirection] = useState<SlideDirection>(0)
  const [loadedPhotoUrls, setLoadedPhotoUrls] = useState<Record<string, true>>({})
  const slideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (slideTimeoutRef.current)
      clearTimeout(slideTimeoutRef.current)
    setCurrentPhotoIndex(0)
    setIsAnimating(false)
    setSlideDirection(0)
  }, [photosWithUrl.length])

  useEffect(() =>
    () => {
      if (slideTimeoutRef.current)
        clearTimeout(slideTimeoutRef.current)
    }, [])

  const currentPhoto = photosWithUrl[currentPhotoIndex] ?? null
  const previousPhoto = photosWithUrl[normalizeIndex(currentPhotoIndex - 1, photosWithUrl.length)] ?? null
  const nextPhoto = photosWithUrl[normalizeIndex(currentPhotoIndex + 1, photosWithUrl.length)] ?? null
  const staticStreetViewUrl = location
    ? createStaticUrl({
      heading: 0,
      location,
      imageWidth,
      imageHeight,
    })
    : null

  const hasMultiplePhotos = photosWithUrl.length > 1
  const trackTransform = isAnimating
    ? slideDirection === 1
      ? 'translateX(-66.6667%)'
      : 'translateX(0%)'
    : 'translateX(-33.3333%)'

  return (
    <Box
      id='property-preview-card'
      borderRadius={2}
      overflow='hidden'
      boxSize='full'
      pos='relative'
      {...props}
    >
      {currentPhoto
        ? (
          <Box boxSize='full' overflow='hidden' bg='grayweak.100'>
            <Box
              display='flex'
              boxSize='full'
              width='300%'
              transform={trackTransform}
              transition={isAnimating ? `transform ${SLIDE_ANIMATION_DURATION_MS}ms ease` : 'none'}
            >
              <PhotoSlide
                photo={previousPhoto}
                imageHeight={imageHeight}
                isLoaded={isPhotoLoaded(loadedPhotoUrls, previousPhoto?.url)}
                onLoad={url => {
                  setLoadedPhotoUrls(prev => ({ ...prev, [url]: true }))
                }}
              />
              <PhotoSlide
                photo={currentPhoto}
                imageHeight={imageHeight}
                isLoaded={isPhotoLoaded(loadedPhotoUrls, currentPhoto.url)}
                onLoad={url => {
                  setLoadedPhotoUrls(prev => ({ ...prev, [url]: true }))
                }}
              />
              <PhotoSlide
                photo={nextPhoto}
                imageHeight={imageHeight}
                isLoaded={isPhotoLoaded(loadedPhotoUrls, nextPhoto?.url)}
                onLoad={url => {
                  setLoadedPhotoUrls(prev => ({ ...prev, [url]: true }))
                }}
              />
            </Box>
          </Box>
        )
        : staticStreetViewUrl
          ? (
            <Image
              src={staticStreetViewUrl}
              boxSize='full'
              objectFit='fill'
              loading='lazy'
            />
          )
          : (
            <Center boxSize='full' bg='card.bg.2'>
              <Text textStyle='tagL' color='grayweak.900'>No image</Text>
            </Center>
          )}

      {hasMultiplePhotos && (
        <>
          <IconButton
            aria-label='Previous photo'
            icon={<ChevronLeftIcon />}
            variant='icon-solid'
            colorScheme='ondark'
            size='sm'
            pos='absolute'
            left={1}
            top='50%'
            transform='translateY(-50%)'
            zIndex={2}
            isDisabled={isAnimating}
            onClick={ev => {
              ev.stopPropagation()
              startSlide({
                direction: -1,
                photosCount: photosWithUrl.length,
                isAnimating,
                setSlideDirection,
                setIsAnimating,
                slideTimeoutRef,
                setCurrentPhotoIndex,
              })
            }}
          />

          <IconButton
            aria-label='Next photo'
            icon={<ChevronRightIcon />}
            variant='icon-solid'
            colorScheme='ondark'
            size='sm'
            pos='absolute'
            right={1}
            top='50%'
            transform='translateY(-50%)'
            zIndex={2}
            isDisabled={isAnimating}
            onClick={ev => {
              ev.stopPropagation()
              startSlide({
                direction: 1,
                photosCount: photosWithUrl.length,
                isAnimating,
                setSlideDirection,
                setIsAnimating,
                slideTimeoutRef,
                setCurrentPhotoIndex,
              })
            }}
          />

          <Box
            textStyle='tagL'
            pos='absolute'
            bottom={1}
            left='50%'
            transform='translateX(-50%)'
            px={1}
            py={0.25}
            borderRadius={1}
            bg='grayweak.200'
            color='graystrong.500'
            borderWidth={0.125}
            borderColor='grayweak.500'
            zIndex={2}
          >
            {currentPhotoIndex + 1}
            /
            {photosWithUrl.length}
          </Box>
        </>
      )}
    </Box>
  )
}

const PhotoSlide = ({
  photo,
  imageHeight,
  isLoaded,
  onLoad,
}: {
  photo: Photo | null
  imageHeight: number
  isLoaded: boolean
  onLoad: (url: string) => void
}) => (
  <Box
    width='33.3333%'
    height='100%'
    position='relative'
    bg='grayweak.100'
  >
    {!photo
      ? (
        <Center boxSize='full'>
          <Text textStyle='tagL' color='grayweak.900'>No image</Text>
        </Center>
      )
      : (
        <>
          {!isLoaded && (
            <Center
              pos='absolute'
              inset={0}
              bg='grayweak.100'
              zIndex={1}
            >
              <Spinner size='sm' color='grayweak.900' />
            </Center>
          )}
          <Image
            src={photo.url}
            boxSize='full'
            objectFit='cover'
            loading='eager'
            opacity={isLoaded ? 1 : 0}
            transition='opacity 120ms linear'
            onLoad={() => {
              onLoad(photo.url)
            }}
            style={{ height: imageHeight }}
          />
        </>
      )}
  </Box>
)

const SLIDE_ANIMATION_DURATION_MS = 220
type SlideDirection = -1 | 0 | 1

const startSlide = ({
  direction,
  photosCount,
  isAnimating,
  setSlideDirection,
  setIsAnimating,
  slideTimeoutRef,
  setCurrentPhotoIndex,
}: {
  direction: -1 | 1
  photosCount: number
  isAnimating: boolean
  setSlideDirection: (direction: SlideDirection) => void
  setIsAnimating: (value: boolean) => void
  slideTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
  setCurrentPhotoIndex: Dispatch<SetStateAction<number>>
}) => {
  if (isAnimating || photosCount <= 1) return

  setSlideDirection(direction)
  setIsAnimating(true)

  slideTimeoutRef.current = setTimeout(() => {
    setCurrentPhotoIndex(prev => normalizeIndex(prev + direction, photosCount))
    setSlideDirection(0)
    setIsAnimating(false)
  }, SLIDE_ANIMATION_DURATION_MS)
}

const normalizeIndex = (index: number, total: number): number => {
  if (total <= 0) return 0
  return (index % total + total) % total
}

const isPhotoLoaded = (
  loadedPhotoUrls: Record<string, true>,
  url?: string,
) => !!url && loadedPhotoUrls[url] === true

const createStaticUrl = ({
  heading,
  location,
  imageWidth,
  imageHeight,
}: {
  heading: number
  location: Location
  imageWidth?: number
  imageHeight?: number
}) => {
  const BASE_URL = 'https://maps.googleapis.com/maps/api/streetview'
  const params = QueryString.stringify({
    size: `${imageWidth}x${imageHeight}`,
    location: `${location.latitude},${location.longitude}`,
    heading,
    fov: 90,
    pitch: 0,
    key: GOOGLE_KEY,
  })

  return `${BASE_URL}?${params}`
}

const createPreviewCardKey = (props: PropertyPreviewCardProps) => {
  const location = props.location
  const photoSignature = props.photos?.map(photo => photo.url).join('|') ?? 'no-photo'

  return [
    `${props.imageWidth}x${props.imageHeight}`,
    location?.latitude ?? 'no-lat',
    location?.longitude ?? 'no-lng',
    photoSignature,
  ].join('-')
}
